<template>
  <div
    :class="classname">
    <div class="header-content__section header-content__section--left">
      <search v-if="hasSearchComputed" />
      <template v-if="!isSearchActive">
        <discover-menu v-if="hasDiscoverMenuComputed" />
        <header-link
          v-if="hasLibraryLink"
          class="header-content__link header-content__link--library"
          id="library"
          :only-login="true"
          :is-main="true" />
        <header-link
          v-if="hasHighlightsLink"
          class="header-content__link header-content__link--highlights"
          id="highlights"
          :only-login="true"
          :is-main="true" />
      </template>
    </div>
    <div class="header-content__section header-content__section--right">
      <slot />
      <header-link
        v-if="hasPricingLink"
        class="header-content__link"
        id="pricing"
        :is-main="true" />
      <header-link
        v-if="hasBusinessLink"
        class="header-content__button"
        id="business"
        :is-main="true" />
      <button-vue
        v-if="cta"
        :class="{
          [cta.classname]: cta.classname,
          'header-content__button': true,
          'js-open-signup-popup': signupPopupEnabled,
          'js-open-login-popup': cta.openLogin
        }"
        :copy="cta.copy"
        :href="ctaHref"
        :data="cta.data"
        :use-router="cta.useRouter"
        event-category="header-20200505-custom_cta" />
      <header-link
        v-if="hasSignupLink"
        class="header-content__button"
        id="signup"
        data-event-category="header-20210521-header_signup_cta"
        :only-logout="true" />
      <header-link
        v-if="hasUpgradeLink && hasUserMenu"
        class="header-content__button header-content__upgrade"
        id="upgrade"
        :only-login="true"
        :only-basic="true" />
      <header-link
        v-if="hasLoginLink"
        class="header-content__link"
        id="login"
        :is-main="true"
        :only-logout="true"
        :data-login-path="inviteCode ? loginPath : ''"
        :data-signup-path="inviteCode ? signupPath: ''"
        :has-js-popup-class="hasJsPopupClass" />
      <user-menu
        v-if="hasUserMenu"
        :has-upgrade-link="hasUpgradeLink" />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Button from "@/shared/button";
import HeaderLink from "./header-link";
import UserMenu from "./user-menu";
import DiscoverMenu from "./discover-menu";
import Search from "./search";
import bemBlockModifiers from "@/utils/bem-block-modifiers";
import globals from "@/utils/globals";

export default {
  name: "HeaderContent",
  components: {
    ButtonVue: Button,
    HeaderLink,
    UserMenu,
    DiscoverMenu,
    Search
  },
  props: {
    hasLoginLink: { type: Boolean, default: false },
    hasJsPopupClass: { type: Boolean, default: true },
    hasSignupLink: { type: Boolean, default: false },
    hasPricingLink: { type: Boolean, default: false },
    hasLibraryLink: { type: Boolean, default: false },
    hasHighlightsLink: { type: Boolean, default: false },
    hasUserMenu: { type: Boolean, default: false },
    hasUpgradeLink: { type: Boolean, default: true },
    hasBusinessLink: { type: Boolean, default: false },
    hasDiscoverMenu: { type: [Boolean, String], default: false },
    hasSearch: { type: [Boolean, String], default: false },
    cta: {
      type: Object,
      default: null,
      validator: function (value) {
        return typeof value.copy === "string"
          && typeof ctaHref
          && (!value.openSignup || typeof value.openSignup === "boolean")
          && (!value.openLogin || typeof value.openLogin === "boolean");
      }
    },
    inviteCode: { type: String, default: "" },
    modifiers: { type: Array, default: () => [] }
  },
  computed: {
    ...mapGetters("header", ["isSearchActive"]),
    ...mapGetters(["isUserLoggedIn"]),
    ...mapGetters("layout", ["startTrialButtonCtaHref"]),
    joinPathAfterLoginOrSignup() {
      return this.inviteCode ? `/${this.$i18n.locale}/nc/join/${this.inviteCode}?fromLoginSignup=true` : "";
    },
    signupPath() {
      return `${globals.routes.signup}?last_page_before_login=${encodeURIComponent(this.joinPathAfterLoginOrSignup)}`;
    },
    loginPath() {
      return `${globals.routes.login}?last_page_before_login=${encodeURIComponent(this.joinPathAfterLoginOrSignup)}`;
    },
    signupPopupEnabled() {
      if (this.startTrialButtonCtaHref) {
        return false;
      }
      return this.cta.openSignup;
    },
    ctaHref() {
      if (this.startTrialButtonCtaHref) {
        return this.startTrialButtonCtaHref;
      }

      return this.cta.href;
    },
    classname() {
      const modifiers = [...this.modifiers];
      if (this.isSearchActive) {
        modifiers.push("search-active");
      }
      return bemBlockModifiers("header-content", modifiers);
    },
    hasDiscoverMenuComputed() {
      if (this.hasDiscoverMenu === "isUserLoggedIn") {
        return this.isUserLoggedIn;
      }
      return this.hasDiscoverMenu;
    },
    hasSearchComputed() {
      if (this.hasSearch === "isUserLoggedIn") {
        return this.isUserLoggedIn;
      }
      return this.hasSearch;
    }
  }
};
</script>

<style lang="scss">
.header-content {
  $root: &;

  display: flex;
  align-items: center;
  height: 100%;

  &__section {
    display: flex;
    align-items: center;
    height: 100%;

    &--left {
      flex-grow: 1;
    }

    /* stylelint-disable-next-line selector-nested-pattern */
    > * {
      margin-left: $spacing-8;

      @include media(min-width, s) {
        margin-left: $spacing-16;
      }

      @include media(min-width, m) {
        margin-left: $spacing-24;
      }

      @include media(min-width, l) {
        margin-left: $spacing-32;
      }
    }
  }

  /* stylelint-disable-next-line selector-nested-pattern */
  * {
    @include font-family(sans-serif);
  }

  &--search-active {

    #{$root}__button {

      @include media(max-width, l) {
        display: none;
      }
    }
  }

  &--right-search {

    #{$root}__section--left {
      justify-content: flex-end;
    }
  }

  @include media(max-width, m) {

    #{$root} {

      &__link {

        &--library {
          display: none;
        }

        &--business {
          display: none;
        }
      }

      &__upgrade {
        display: none;
      }
    }
  }

  @include media(max-width, l) {

    #{$root}__link {

      &--highlights {
        display: none;
      }
    }
  }
}
</style>
